import React from "react";
import styled from "styled-components";
import CaseStudiesCard from "../CaseStudiesCard";
import Rule from "../../../Rule";
import Heading from "../../../Heading";
import Image from "../../../Image";
import Box from "../../../Box";
import BlogLayout from "../BlogLayout";
import Stack from "../../../Stack";
import DumptyCover from "../../../../img/casestudy/dumpty.png";

const DumptyCard = styled(CaseStudiesCard)`
  p {
    max-width: 80ch;
  }
`;

const TwoUpGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--spacing-medium);
  @media (min-width: 700px) {
    gap: var(--spacing-large);
    grid-template-columns: 1fr 1fr;
  }
`;

function DumptyCaseStudy() {
  return (
    <DumptyCard id="dumpty">
      <Heading
        variant="smaller"
        style={{ marginTop: 0, marginBottom: "var(--spacing-x-tiny)", textTransform: "uppercase" }}
      >
        Product Design
      </Heading>
      <p style={{ marginBottom: 0, fontSize: "var(--font-size-text-medium)" }}>
        Designing a native macOS menu bar app for fast snippet, prompt, and template reuse.
      </p>
      <Rule spacing="medium" inverted mt="var(--spacing-medium)" mb="var(--spacing-large)" />

      <BlogLayout wide>
        <Stack spacing="var(--spacing-large)">
          <Box>
            <Heading variant="larger" style={{ marginBottom: 0 }}>
              Dumpty
            </Heading>
            <p
              style={{
                fontSize: "var(--font-size-heading-small)",
                marginBottom: "var(--spacing-tiny)",
                maxWidth: "80ch",
              }}
            >
              A lightweight desktop companion for storing, searching, and copying reusable code snippets, AI prompts,
              and working templates without breaking flow.
            </p>
          </Box>

          <TwoUpGrid>
            <Box>
              <Heading variant="small">Context</Heading>
              <p>
                Daily product work involves a lot of repeated text: code snippets, terminal commands, design system
                notes, component examples, release copy, and increasingly, detailed AI prompts. Those fragments are
                useful, but they often live across notes, docs, chats, and repos, which makes reuse slower than it
                should be.
              </p>
            </Box>
            <Box>
              <Heading variant="small">Opportunity</Heading>
              <p>
                Dumpty creates a focused retrieval layer for those fragments. The opportunity was to design a small,
                native macOS utility that opens instantly from the menu bar, filters with search, and copies the right
                snippet in one action.
              </p>
            </Box>
          </TwoUpGrid>

          <Image
            dark
            src={DumptyCover}
            alt="Dumpty macOS menu bar application screen"
            caption="Dumpty menu bar utility"
          />

          <TwoUpGrid>
            <Box>
              <Heading variant="small">Menu Bar Constraints</Heading>
              <p>
                The menu bar sets a tight interaction model. The window has to feel temporary, readable, and quick to
                dismiss. That shaped the interface around search-first navigation, compact rows, clear snippet labels,
                and enough preview content to choose confidently without turning the utility into a full document
                manager.
              </p>
            </Box>
            <Box>
              <Heading variant="small">Interaction Model</Heading>
              <p>
                The intended loop is direct: open Dumpty, type a few characters, select a result, copy, and return to the
                original task. Keeping the primary action close to the result list makes the product useful for both
                small snippets and longer prompt templates.
              </p>
            </Box>
          </TwoUpGrid>

          <Box>
            <Heading variant="small">Product Direction</Heading>
            <p>
              Dumpty sits between a clipboard manager and a knowledge base. It is intentionally narrower than both:
              persistent enough to hold reusable material, but lightweight enough to stay in the user’s flow. That
              positioning helped keep the product decisions focused on speed, search quality, local control, and a
              native desktop feel.
            </p>
          </Box>
        </Stack>
      </BlogLayout>
    </DumptyCard>
  );
}

export default DumptyCaseStudy;
